import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Hook to create a provider.
 * Returns a react-query mutation that accepts `{ name: string; description?: string }`.
 * Invalidates the providers cache to trigger a refetch.
 */
export function useCreateProvider(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.createProvider(tenantId),
    mutationFn: async (payload: { name: string; description?: string }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const provider = await providerService.createProvider(tenantId, {
        name: payload.name,
        description: payload.description,
      });

      return pollForResource(() =>
        providerService.findProvider(tenantId, provider.id)
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: providerQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
